import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    ModuleRegistry,
    RowClickedEvent,
    SelectionChangedEvent,
    SortChangedEvent,
} from 'ag-grid-community';
import {
    AllCommunityModule,
    ServerSideRowModelApiModule,
    ServerSideRowModelModule,
} from 'ag-grid-enterprise';

import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { WdxIsLoadingModule } from '@wdx/shared/components/wdx-is-loading';
import { FilterHttpService } from '@wdx/shared/infrastructure/api-service';
import {
    BaseQueryExpression,
    FilterFieldType,
    FilterOperator,
    IdentityFilterOption,
    IMeLocale,
    Query,
    SortDirection,
    SystemEntity,
    TRANSLATION_COLUMN_GROUP,
    TranslationsService,
    ViewColumn,
    WdxAgGridThemeDirective,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { AgGridAngular } from 'ag-grid-angular';
import * as R from 'ramda';
import { debounceTime, Observable, Subject, take, takeUntil } from 'rxjs';
import {
    AG_GRID_LOCALES,
    BULK_SELECT_ROW_SELECTION,
    GroupByConfig,
    SERVER_SIDE_DEFAULT_GRID_OPTIONS,
    wdxServerSideGetRowId,
} from '../../constants/entity-ag-grid-config';
import { DYNAMIC_GROUP_BY_VALUE_GETTER, getStringValue } from '../../utils';
import { WdxMultiSelectBannerComponent } from '../multi-select-banner';

ModuleRegistry.registerModules([
    AllCommunityModule,
    ServerSideRowModelModule,
    ServerSideRowModelApiModule,
]);

export type WdxColDef = ColDef & {
    wdx?: {
        sortTarget?: string;
    };
    viewColumn?: ViewColumn;
};

@Component({
    selector: 'wdx-ag-grid-entity-table',
    templateUrl: './entity-ag-grid-table.component.html',
    styleUrls: ['./entity-ag-grid-table.component.scss'],
    imports: [
        AgGridAngular,
        WdxAgGridThemeDirective,
        WdxIsLoadingModule,
        WdxMultiSelectBannerComponent,
    ],
    host: { class: 'h-100 d-flex flex-column' },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityAgGridTableComponent
    extends WdxDestroyClass
    implements OnChanges, OnInit
{
    private filterHttpService = inject(FilterHttpService);
    private translationsService = inject(TranslationsService);
    private localeService = inject(IMeLocale);

    private refreshTable$ = new Subject<number>();
    private _initialSort?: {
        sortBy?: string | null;
        sortDirection?: SortDirection;
    };

    @Input() loading = false;
    @Input() gridOptions: GridOptions = SERVER_SIDE_DEFAULT_GRID_OPTIONS;
    @Input() groupBy: GroupByConfig | null = null;
    @Input() localeCode: string | null | undefined;

    @Input() set initialSort(
        value: Pick<Query, 'sortBy' | 'sortDirection'> | undefined,
    ) {
        if (
            !value ||
            this._initialSort?.sortBy !== value.sortBy ||
            this._initialSort?.sortDirection !== value.sortDirection
        ) {
            this._initialSort = {
                sortBy: value?.sortBy,
                sortDirection: value?.sortDirection,
            };
        }
    }

    @Input() getCube!: (
        entityType: SystemEntity,
        options: any,
    ) => Observable<any>;

    public columnApi!: any;
    private _columnDefs: WdxColDef[] = [];
    @Input() set columnDefs(value: WdxColDef[]) {
        // doing this old school matching comparison as Ramda equals doesn't work reliable for this object
        if (JSON.stringify(value) === JSON.stringify(this._columnDefs)) {
            return;
        }
        this._columnDefs = value;
        if (this.gridApi) {
            this.gridApi.setGridOption('columnDefs', []);
            // Promise.resolve/settimeout required to ensure the column headers update properly
            // we need to set the columnDefs to empty array, then allow time for that apply before setting our desired config
            Promise.resolve().then(() => {
                this.gridApi?.setGridOption('columnDefs', value);
                this.refreshTable$.next(Date.now());
                // Auto-size columns without flex after column definitions are updated
                this.autoSizeColumnsWithoutFlex();
            });
        }
    }
    get columnDefs(): WdxColDef[] {
        return this._columnDefs;
    }
    private _entityType!: SystemEntity;
    @Input() set entityType(value: SystemEntity) {
        this._entityType = value;
        if (this.gridApi) {
            this.refreshTable$.next(Date.now());
        }
    }
    private _queryExpressions: BaseQueryExpression[] = [];
    @Input() set queryExpressions(
        value: BaseQueryExpression | BaseQueryExpression[],
    ) {
        if (R.equals(this._queryExpressions, value)) {
            return;
        }
        if (!Array.isArray(value)) {
            this._queryExpressions = [value];
        } else {
            this._queryExpressions = value;
        }
        if (this.gridApi) {
            this.refreshTable$.next(Date.now());
        }
    }

    private _bulkSelect = false;
    @Input() set bulkSelect(value: boolean | undefined) {
        this._bulkSelect = Boolean(value);
        this.applyBulkSelect();
    }
    get bulkSelect(): boolean {
        return this._bulkSelect;
    }

    @Input() pageSize = 50;
    private _searchText?: string;
    @Input() set searchText(value: string | undefined) {
        this._searchText = value;
        this.refreshTable$.next(Date.now());
    }
    @Input() set showLoading(timestamp: number | undefined) {
        if (timestamp) {
            this.gridApi?.setGridOption('loading', true);
        } else {
            this.gridApi?.hideOverlay();
        }
    }
    @Input() set refresh(timestamp: number | undefined) {
        if (timestamp) {
            this.gridApi?.hideOverlay();
            this.refreshTable$.next(Date.now());
        }
    }

    private _searchFilterOption: IdentityFilterOption = {
        filterName: FilterFieldType.Search,
    };
    @Input() set searchFilterOption(value: IdentityFilterOption) {
        if (value && !R.equals(this._searchFilterOption, value)) {
            this._searchFilterOption = value;
            this.refreshTable$.next(Date.now());
        }
    }

    @Output() rowCount = new EventEmitter<number>();
    @Output() sortChange = new EventEmitter<Query>();
    @Output() agGridApi = new EventEmitter<GridApi>();
    @Output() tableDataLoaded = new EventEmitter<void>();
    @Output() selectionChanged = new EventEmitter<any[]>();
    private gridApi?: GridApi;

    public selectedRows: any[] = [];
    public totalRecords = 0;

    public defaultColDef: ColDef = {
        cellClass: [],
    };

    private _onRowClicked: (event: RowClickedEvent) => void = () => undefined;

    @Input() set onRowClicked(value: (event: RowClickedEvent) => void) {
        if (!value) {
            this._onRowClicked = () => undefined;
            if (
                (this.defaultColDef.cellClass as string[]).includes('pointer')
            ) {
                this.defaultColDef.cellClass = (
                    this.defaultColDef.cellClass as string[]
                ).filter((x) => x !== 'pointer');
            }
            return;
        }
        this._onRowClicked = value;
        if ((this.defaultColDef.cellClass as string[]).includes('pointer')) {
            return;
        }
        (this.defaultColDef.cellClass as string[]).push('pointer');
    }

    public get onRowClicked(): (event: RowClickedEvent) => void {
        return this._onRowClicked;
    }

    public localeText: GridOptions['localeText'];

    ngOnInit() {
        const lang =
            this.localeService.getMeLocale()?.code?.split('-')[0] || 'en';
        this.localeText = AG_GRID_LOCALES[lang] || AG_GRID_LOCALE_EN;
        if (!this.gridOptions.getRowId) {
            this.gridOptions = {
                ...this.gridOptions,
                getRowId: wdxServerSideGetRowId,
            };
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['groupBy'] && this.gridApi && !this.gridApi.isDestroyed()) {
            this.configureGrouping();
        }
    }

    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        if (this._bulkSelect) {
            this.applyBulkSelect();
        }
        this.refreshTable$
            .pipe(takeUntil(this.destroyed$), debounceTime(100))
            .subscribe(() => {
                if (this.gridApi && !this.gridApi.isDestroyed()) {
                    if (this.selectedRows.length) {
                        this.gridApi.deselectAll();
                    }
                    this.gridApi?.refreshServerSide({ purge: true });
                }
            });
        this.agGridApi.emit(params.api);
        this.updateDataSource();

        params.api.addEventListener('firstDataRendered', () => {
            this.autoSizeColumnsWithoutFlex();
        });
    }

    public onGridSelectionChanged(event: SelectionChangedEvent) {
        this.selectedRows = event.api.getSelectedRows();
        this.selectionChanged.emit(this.selectedRows);
    }

    public clearSelection() {
        this.gridApi?.deselectAll();
    }

    private applyBulkSelect() {
        if (!this.gridApi || this.gridApi.isDestroyed()) {
            return;
        }
        if (this._bulkSelect) {
            this.gridApi.setGridOption(
                'rowSelection',
                BULK_SELECT_ROW_SELECTION,
            );
        } else {
            this.gridApi.deselectAll();
            this.gridApi.setGridOption('rowSelection', undefined);
        }
    }

    public autoGroupColumnDef(): ColDef {
        return {
            headerName:
                this.groupBy?.label ??
                this.translationsService.getTranslationByKey(
                    TRANSLATION_COLUMN_GROUP,
                ),
            field: this.groupBy?.name,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                innerRenderer: (params: any) => {
                    const label = getStringValue(params.data, 'groups');
                    const count = params.data?.rollupValue;
                    return label && count ? `${label} (${count})` : label || '';
                },
            },
        };
    }

    private configureGrouping() {
        if (!this.gridApi) {
            return;
        }

        this.groupBy?.name
            ? this.applyGroupedColumnDefs()
            : this.removeGroupColumnDefs();

        this.gridApi.setGridOption(
            'serverSideDatasource',
            this.groupBy?.name
                ? this.groupedDataSource(this.groupBy.name)
                : this.entityDataSource(),
        );
    }

    private applyGroupedColumnDefs() {
        const groupField = this.groupBy!.name;
        const updatedDefs: WdxColDef[] = [
            {
                field: groupField,
                rowGroup: true,
                hide: true,
                headerName: groupField,
                valueGetter: DYNAMIC_GROUP_BY_VALUE_GETTER(groupField),
            },
            ...this.columnDefs
                .filter((col) => col.field !== groupField)
                .map((col) => ({ ...col })),
        ];

        this.gridApi!.setGridOption('columnDefs', []);
        Promise.resolve().then(() =>
            this.gridApi!.setGridOption('columnDefs', updatedDefs),
        );
    }

    private removeGroupColumnDefs() {
        this.gridApi?.setGridOption('columnDefs', []);
        Promise.resolve().then(() => {
            this.gridApi?.setGridOption(
                'columnDefs',
                this.columnDefs.map((col) => {
                    if (col.rowGroup) {
                        return {
                            ...col,
                            hide: false,
                            rowGroup: false,
                            valueGetter: undefined,
                        };
                    }
                    return col;
                }),
            );
            this.gridApi?.setGridOption(
                'serverSideDatasource',
                this.entityDataSource(),
            );
        });
    }

    private groupedDataSource(groupField: string): IServerSideDatasource {
        return {
            getRows: (params) => {
                const isTopLevel = !params.request.groupKeys?.length;

                if (isTopLevel) {
                    const expressions = [...this._queryExpressions];
                    if (this._searchText) {
                        expressions.push({
                            field: FilterFieldType.Search,
                            operator: FilterOperator.Matches,
                            values: [this._searchText],
                        });
                    }

                    this.getCube(this._entityType, {
                        groups: [groupField],
                        expressions,
                    })
                        .pipe(take(1))
                        .subscribe({
                            next: (rows: any) => {
                                this.totalRecords = rows.length;
                                this.rowCount.emit(rows.length);

                                params.success({
                                    rowData: rows.map(
                                        (row: any, i: number) => ({
                                            ...row,
                                            ...(row.groups ?? {}),
                                            __group: true,
                                            __id__: String(i),
                                        }),
                                    ),
                                    rowCount: rows.length,
                                });
                            },
                            error: () => params.fail(),
                        });

                    return;
                }

                const node = params.parentNode;
                const groupValue = getStringValue(node.data, groupField);

                const expressions = [...this._queryExpressions];
                if (this._searchText) {
                    expressions.push({
                        field: FilterFieldType.Search,
                        operator: FilterOperator.Matches,
                        values: [this._searchText],
                    });
                }
                expressions.push({
                    field: groupField,
                    operator: FilterOperator.Equals,
                    values: [groupValue],
                });

                const page = Math.ceil(
                    (params.request.endRow || 0) /
                        (this.gridOptions.cacheBlockSize || 50),
                );

                this.loadData(
                    page,
                    this._entityType,
                    expressions,
                    this.gridOptions.cacheBlockSize || 50,
                )
                    .pipe(take(1))
                    .subscribe({
                        next: (data) => {
                            params.success({
                                rowData: data.results.map((row: any, i) => ({
                                    ...row,
                                    __id__: `${groupValue}_${page}_${i}`,
                                })),
                                rowCount: data.paging.totalRecords,
                            });
                        },
                        error: () => params.fail(),
                    });
            },
        };
    }

    private autoSizeColumnsWithoutFlex() {
        if (!this.gridApi || this.gridApi.isDestroyed()) {
            return;
        }

        const columnsToAutoSize = this.columnDefs
            .filter((colDef) => !colDef.flex && colDef.colId)
            .map((colDef) => colDef.colId)
            .filter((colId): colId is string => Boolean(colId));

        if (columnsToAutoSize.length > 0) {
            this.gridApi.autoSizeColumns(columnsToAutoSize, false);
        }
    }

    public onSortChanged(params: SortChangedEvent) {
        this._initialSort = undefined;
        const column = params.columns?.find((col) => Boolean(col.getSort()));
        if (column) {
            const sortConfig = {
                sortBy: column.getColId(),
                sortDirection:
                    column.getSort() === 'asc'
                        ? SortDirection.Ascending
                        : column.getSort() === 'desc'
                          ? SortDirection.Descending
                          : undefined,
            };
            this.sortChange.emit(sortConfig);
        } else {
            this.sortChange.emit({
                sortDirection: undefined,
                sortBy: undefined,
            });
        }
    }

    private updateDataSource() {
        const dataSource = this.entityDataSource();

        this.gridApi?.setGridOption('serverSideDatasource', dataSource);
    }

    private loadData(
        pageNumber: number,
        entityType: SystemEntity,
        queryExpressions: BaseQueryExpression[] = [],
        pageSize = 50,
        sortBy?: string,
        sortDirection?: SortDirection,
    ) {
        const sorting = this._initialSort
            ? this._initialSort
            : sortBy && sortDirection
              ? {
                    sortBy,
                    sortDirection,
                }
              : {};

        if (sorting.sortBy && sorting.sortDirection) {
            const colId = this.columnDefs.find(
                ({ viewColumn }) => viewColumn?.sortKey === sorting.sortBy,
            )?.colId;
            this.gridApi?.applyColumnState({
                ...(colId
                    ? {
                          state: [
                              {
                                  colId,
                                  sort:
                                      sorting.sortDirection ===
                                      SortDirection.Ascending
                                          ? 'asc'
                                          : 'desc',
                              },
                          ],
                      }
                    : {}),
                defaultState: { sort: null },
            });
        } else {
            this.gridApi?.applyColumnState({
                defaultState: { sort: null },
            });
        }

        return this.filterHttpService.getRecords(
            entityType,
            { pageSize, pageNumber },
            {
                expressions: queryExpressions,
                ...sorting,
            },
        );
    }

    private entityDataSource(): IServerSideDatasource {
        return {
            getRows: (params) => {
                this.getRows(params);
            },
        };
    }

    private getRows(params: IServerSideGetRowsParams<any, any>) {
        const page = Math.ceil(
            (params.request.endRow || 0) /
                (this.gridOptions.cacheBlockSize || 50),
        );
        const sortBy = this.columnDefs.find(
            (col) => col.colId === params.request.sortModel?.[0]?.colId,
        )?.wdx?.sortTarget;
        const sort = params.request.sortModel?.[0]?.sort;
        const sortDirection =
            sort === 'asc'
                ? SortDirection.Ascending
                : sort === 'desc'
                  ? SortDirection.Descending
                  : undefined;
        const queries = [
            ...this._queryExpressions,
            ...(this._searchText
                ? [
                      {
                          field: this._searchFilterOption.filterName,
                          operator: FilterOperator.Matches,
                          values: [
                              this._searchFilterOption.code
                                  ? {
                                        systemCode:
                                            this._searchFilterOption.code,
                                        search: this._searchText,
                                    }
                                  : this._searchText,
                          ],
                      },
                  ]
                : []),
        ] as BaseQueryExpression[];

        this.loadData(
            page,
            this._entityType,
            queries,
            this.pageSize,
            sortBy,
            sortDirection,
        )
            .pipe(take(1))
            .subscribe({
                next: (data) => {
                    this.totalRecords = data.paging.totalRecords;
                    this.rowCount.emit(data.paging.totalRecords);
                    params.success({
                        rowData: data.results,
                        rowCount: data.paging.totalRecords,
                    });
                    setTimeout(() => {
                        this.tableDataLoaded.emit();
                    }, 0);
                },
                error: () => params.fail(),
            });
    }
}
